import { useRef, useCallback, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { useIsMobile } from '../../../hooks/useMediaQuery';

import { useDispatch, useSelector } from 'react-redux';
import { selectUserToken } from '../../../redux/features/user/userSelectors';
import { setTotalSearchResults } from '../../../redux/features/dashboard/dashboardSlice';
import {
  selectSearchFilters,
  selectSearchLikeFilter,
  selectSearchQuery,
  selectSearchQueryRequest,
  selectSearchType,
  selectShowMap,
  selectSortBy,
} from '../../../redux/features/dashboard/dashboardSelectors';
import {
  useGetSearchRealtiesMapQuery,
  useGetSearchRealtiesInfiniteQuery,
} from '../../../redux/services/search/searchApi';
import { useRequestLikes } from '../../../hooks/useRequestLikes';

import { ErrorMsg } from '../ErrorMsg';
import { CardRealty } from '../realtyCard/CardRealty';
import { LoaderProgress } from '../LoaderProgress';
import { Loader } from '../Loader';
import Map from '../mapComponent/Map';
import { ButtonTemplate } from '../buttons/ButtonTemplate';
import { SearchToolbar } from '../SearchToolbar';

import { errorMessages } from '../../../data/constants';
import { ArrowUp } from 'lucide-react';
import { toast } from 'sonner';

export const RealtyListSearch = ({ setIsLoading = () => {} }) => {
  const dispatch = useDispatch();

  const userToken = useSelector(selectUserToken);
  const searchType = useSelector(selectSearchType) || '1';
  const searchQuery = useSelector(selectSearchQuery) || '';
  const searchQueryRequest = useSelector(selectSearchQueryRequest) || '';
  const searchFilters = useSelector(selectSearchFilters) || '';
  const sortBy = useSelector(selectSortBy);
  const showMapRedux = useSelector(selectShowMap);
  const isMobile = useIsMobile();
  const showMap = showMapRedux && !isMobile;
  const likeFilter = useSelector(selectSearchLikeFilter); // null | 'like' | 'dislike'

  const lastScrollRef = useRef(0);
  const listContainerRef = useRef(null);

  // Normal search — only when not in like mode
  const {
    data: realtiesSearch,
    isFetching,
    error: searchError,
    hasNextPage: hasMore,
    fetchNextPage,
  } = useGetSearchRealtiesInfiniteQuery(
    {
      token: userToken,
      search_type: searchType,
      search_query: searchQuery,
      search_query_request: searchQueryRequest,
      search_filters: searchFilters,
      sort_by: sortBy,
    },
    { skip: !!likeFilter || (!searchQuery && !searchFilters) }
  );

  const {
    likedRealties,
    dislikedRealties,
    isFetching: isLikeFetching,
  } = useRequestLikes();
  const activelikeRealties =
    likeFilter === 'like' ? likedRealties : dislikedRealties;
  const { data: realtiesMap = [], isFetching: isRealtiesMapFetching } =
    useGetSearchRealtiesMapQuery(
      {
        token: userToken,
        search_query: searchQuery,
        search_filters: searchFilters,
      },
      { skip: !!likeFilter || !showMap || (!searchQuery && !searchFilters) }
    );

  // Pages can overlap when listings shift between fetches
  const accumulated = useMemo(() => {
    const seen = new Set();
    return (realtiesSearch?.pages ?? [])
      .flatMap(({ realties = [] }) => realties)
      .filter(({ id }) => !seen.has(id) && seen.add(id));
  }, [realtiesSearch?.pages]);
  const total = realtiesSearch?.pages?.at(-1)?.links?.total || 0;
  const activeFetching = likeFilter ? isLikeFetching : isFetching;

  useEffect(() => {
    dispatch(setTotalSearchResults(total));
  }, [total, dispatch]);

  useEffect(() => {
    if (searchError) {
      toast.error(errorMessages.searchFailed);
    }
  }, [searchError]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleScroll = useCallback(() => {
    if (activeFetching) return;

    const isMapView = showMap && listContainerRef.current;
    const scrollTop = isMapView
      ? listContainerRef.current.scrollTop
      : window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = isMapView
      ? listContainerRef.current.scrollHeight
      : document.documentElement.scrollHeight;
    const clientHeight = isMapView
      ? listContainerRef.current.clientHeight
      : window.innerHeight;

    const nearBottom = scrollTop + clientHeight >= scrollHeight - 200;
    const scrollingDown = scrollTop > lastScrollRef.current;
    lastScrollRef.current = scrollTop;

    if (nearBottom && scrollingDown && hasMore) {
      fetchNextPage();
    }
  }, [activeFetching, showMap, hasMore, fetchNextPage]);

  const handleScrollToTop = useCallback(() => {
    if (showMap && listContainerRef.current) {
      listContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showMap]);

  useEffect(() => {
    const target = showMap ? listContainerRef.current : window;
    if (!target) return;

    target.addEventListener('scroll', handleScroll);
    return () => target.removeEventListener('scroll', handleScroll);
  }, [showMap, handleScroll]);
  return (
    <>
      <SearchToolbar
        currentCount={
          likeFilter ? activelikeRealties.length : accumulated.length
        }
        totalCount={likeFilter ? activelikeRealties.length : undefined}
      />

      {likeFilter ? (
        <div className="flex gap-3 flex-wrap justify-center md:justify-start">
          {activelikeRealties.map(realty => (
            <CardRealty
              key={realty?.id}
              realty={realty}
            />
          ))}
        </div>
      ) : (
        accumulated.length > 0 && (
          <div className="flex flex-col-reverse gap-3 w-full h-full lg:flex-row">
            <div
              ref={listContainerRef}
              className={cn(
                'flex gap-3 h-max',
                {
                  'w-full flex-wrap justify-center md:justify-start': !showMap,
                },
                {
                  'flex-col w-full h-[500px] sm:h-[600px] md:h-[700px] lg:h-[750px] 3xl:h-[800px] xs:max-w-[360px] overflow-y-auto hide-scroll':
                    showMap,
                }
              )}
            >
              {accumulated.map(realty => (
                <CardRealty
                  key={realty?.id}
                  realty={realty}
                />
              ))}

              {accumulated.length > 9 && (isFetching || !hasMore) && (
                <div
                  className={cn(
                    'flex flex-col items-center justify-center gap-2 w-full p-6 border border-gray-200 rounded-lg bg-gray-50',
                    { 'xs:w-[360px]': !showMap }
                  )}
                >
                  {isFetching ? (
                    <>
                      <Loader
                        width={32}
                        height={32}
                      />
                      <span className="text-xs text-gray-500 mt-1">
                        Loading more items...
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-600 font-medium">
                        {total
                          ? `${accumulated.length} of ${total} items loaded`
                          : `${accumulated.length} items loaded`}
                      </span>
                      <span className="text-xs text-gray-500">
                        You have reached the end
                      </span>
                      <ButtonTemplate
                        icon={ArrowUp}
                        isIconText={true}
                        text="Back to top"
                        handleClick={handleScrollToTop}
                        classes="mt-2 bg-white border-gray-300 hover:bg-gray-100"
                        color="#374151"
                        size={16}
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {showMap && (
              <div className="relative flex w-full h-[500px] sm:h-[600px] md:h-[700px] lg:h-[750px] 3xl:h-[800px]">
                <Map
                  realties={realtiesMap}
                  isLoading={isRealtiesMapFetching}
                />
              </div>
            )}
          </div>
        )
      )}

      {isFetching && accumulated.length === 0 && !likeFilter && (
        <LoaderProgress />
      )}

      {searchError && (
        <div className="flex justify-center w-full h-20">
          <ErrorMsg message={errorMessages.data} />
        </div>
      )}
    </>
  );
};
